// import { WindowLocation } from '@reach/router';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import ChevronLeftSmall from '@/assets/icons/chevron-left-small.svg';
import ChevronRightSmall from '@/assets/icons/chevron-right-small.svg';
import CloseIcon from '@/assets/icons/close.svg';
import FilterIcon from '@/assets/icons/filter.svg';

import {
  _pageSize,
  _searchDelay,
} from '../../../helpers/custom-list-page/custom-list-page-constants';
import { ListCategoryCard } from '../../../helpers/custom-list-page/interfaces/custom-list-card.interface';
import { isBrowser } from '../../../helpers/is-browser';
import {
  ListCard,
  ListType,
} from '../../../helpers/list-page/interfaces/list-card.interface';
import { calculateActiveFilters } from '../../../helpers/list-page/list-page-helpers';
import { normalizeTextString } from '../../../helpers/normalizeTextString';
import { sortByPriority } from '../../../helpers/utils';
import { useDebounce } from '../../../hooks/use-debounce';
import { IGtmParameters } from '../../../hooks/use-send-gtm-event';
import { Breadcrumbs } from '../../breadcrumbs';
import ImageWrapper from '../../image-wrapper/Image-wrapper';
import Backdrop from '../../ui/backdrop/backdrop';
import InputField from '../../ui/input-field/input-field';
import { NoHits } from '../list-page/no-hits';
import ShowMore from '../list-page/show-more';
import { TileView } from '../list-page/tile-view';
import Image from 'next/image';
import {
  useGetLibraryConstantsDictionaryData,
  useIsMPLivliFamily,
} from '@/providers/ConfigProvider';
import { useUser } from '@/providers/UserProvider';

interface ListPageProps {
  categoryList: ListCategoryCard[];
  subCategoryList: ListCard[];
  pageName: string;
  pageSlug: string;
  node_locale: string;
  // location: any;
  homeText: string;
  breadcrumbs: string[];
  tileColClass: string;
  listType: ListType;
  pageDescription?: string;
  isWebView?: boolean;
  categoryText?: string;
  initialPage?: number;
}

const CustomListPage = ({
  categoryList,
  subCategoryList,
  pageName,
  pageSlug,
  node_locale,
  // location,
  homeText,
  breadcrumbs,
  tileColClass,
  listType,
  pageDescription,
  isWebView,
  categoryText,
  initialPage = 1,
}: ListPageProps) => {
  const isLivliFamily = useIsMPLivliFamily();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [activeFilterString, setActiveFilterString] = useState<string | null>(
    null,
  );
  const [scrollPositionOnFilter, setScrollPositionOnFilter] =
    useState<number>(0);

  const { user } = useUser();

  const defaultSelectedOption = {
    optionText: '',
    optionValue: '',
  };
  const [activeFilterCategory, setActiveFilterCategory] = useState(
    defaultSelectedOption,
  );

  const debouncedFilterString = useDebounce<string>(
    activeFilterString ?? '',
    _searchDelay,
  );

  const debouncedGtmFilterString = useDebounce<string>(
    activeFilterString ?? '',
    _searchDelay * 3, // 3 times longer delay for GTM events to reduce the number of events sent with partial search strings
  );

  const debouncedCategoryString = useDebounce<string>(
    activeFilterCategory?.optionValue ?? '',
    _searchDelay,
  );

  const filterFunctionQuery = (card: ListCard) => {
    if (!debouncedFilterString) return true;

    const matchesName = normalizeTextString(card.name).includes(
      normalizeTextString(debouncedFilterString),
    );

    const matchChildren = card?.children?.some((child) => {
      return normalizeTextString(child.name).includes(
        normalizeTextString(debouncedFilterString),
      );
    });
    return matchChildren || matchesName;
  };

  const filterFunctionCategory = (card: ListCard) => {
    if (!debouncedCategoryString) return true;

    // Find the category under which the card should be filtered
    const selectedCategory = categoryList?.find(
      (category) => category.name === debouncedCategoryString,
    );

    if (!selectedCategory) return false;

    // Get the list of subcategory IDs under the selected category
    const subCategoryIds =
      selectedCategory.subCategories?.map(
        (subCategory) => subCategory?.sys?.id,
      ) || [];

    // Return true if the card ID exists in the subcategory IDs
    return subCategoryIds.includes(card.id);
  };

  const [filteredCardList, setFilteredCardList] = useState<ListCard[]>(
    subCategoryList
      ?.filter(filterFunctionQuery)
      ?.filter(filterFunctionCategory)
      ?.filter(
        (eachItem, idx, list) =>
          list.findIndex((item) => item.id === eachItem.id) === idx,
      ),
  );

  const [page, setPage] = useState(() => {
    if (isBrowser) {
      const pageParam = searchParams.get('page');
      return pageParam ? parseInt(pageParam, 10) : 1;
    }
    return 1;
  });

  const isInitialMount = useRef(true);

  const [isMobileSearchFilterOpen, setIsMobileSearchFilterOpen] =
    useState<boolean>(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState<number>(
    calculateActiveFilters(
      isMobileSearchFilterOpen ? activeFilterString : null,
      activeFilterCategory?.optionValue ?? '',
    ),
  );

  const handleOpenMobileSearchFilter = () => {
    setIsMobileSearchFilterOpen(!isMobileSearchFilterOpen);
  };

  const resetFilters = () => {
    setActiveFilterString(null);
    setActiveFilterCategory(defaultSelectedOption);
    setIsMobileSearchFilterOpen(false);
    setFilteredCardList(
      [...subCategoryList]?.filter(
        (eachItem, idx, list) =>
          list.findIndex((item) => item.id === eachItem.id) === idx,
      ),
    );
  };
  const resetCategory = () => {
    setActiveFilterCategory(defaultSelectedOption);
    setIsMobileSearchFilterOpen(false);
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      setPage(1);
    }
    setFilteredCardList(() =>
      subCategoryList
        ?.filter(filterFunctionQuery)
        ?.filter(filterFunctionCategory)
        ?.filter(
          (eachItem, idx, list) =>
            list.findIndex((item) => item.id === eachItem.id) === idx,
        ),
    );
    setActiveFiltersCount(
      calculateActiveFilters(debouncedFilterString, debouncedCategoryString),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedCategoryString,
    debouncedFilterString,
    node_locale,
    pageSlug,
    debouncedGtmFilterString,
  ]);

  useEffect(() => {
    sendGtmEvents(debouncedCategoryString, debouncedGtmFilterString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCategoryString, debouncedGtmFilterString]);

  const sendGtmEvents = (category: string, filter: string) => {
    if ((category || filter) && listType === 'service') {
      const gtmEventName = `new_${listType}_search`;
      const gtmParams: IGtmParameters = {
        event: gtmEventName,
        search_term: filter ? filter : 'No search query. Filter used',
        search_category: category.length > 0 ? category : 'all',
        user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
      };

      dataLayer.push(gtmParams);
    }
  };

  // Read URL params on mount and when they change
  useEffect(() => {
    const category = searchParams.get('category');
    const search = searchParams.get('search');
    const pageParam = searchParams.get('page');

    if (category) {
      setActiveFilterCategory({
        optionText: category,
        optionValue: category,
      });
    }

    if (search) {
      setActiveFilterString(search);
    }

    if (pageParam) {
      const parsedPage = parseInt(pageParam, 10);
      if (!isNaN(parsedPage) && parsedPage > 0) {
        setPage(parsedPage);
      }
    }
  }, []);

  useEffect(() => {
    const handleUrl = () => {
      if (isBrowser) {
        setScrollPositionOnFilter(window.scrollY);

        // const url = new URL(window.location.href);
        const params = new URLSearchParams(searchParams.toString());
        const paramsBefore = params.toString();
        if (debouncedCategoryString) {
          params.set('category', debouncedCategoryString);
        } else {
          params.delete('category');
        }

        if (debouncedFilterString) {
          params.set('search', debouncedFilterString);
        } else {
          params.delete('search');
        }

        if (page > 1) {
          params.set('page', page.toString());
        } else {
          params.delete('page');
        }

        const paramsAfter = params.toString();
        const searchString =
          paramsAfter.length > 0 ? `?${paramsAfter}` : pathname;
        if (paramsBefore !== paramsAfter) {
          router.push(searchString, { scroll: false });
          // Restore scroll position after a delay
          // if (scrollTimeoutRef.current) {
          //   clearTimeout(scrollTimeoutRef.current);
          // }
          setTimeout(() => {
            window.scrollTo(0, scrollPositionOnFilter);
          }, _searchDelay);
        }
      }
    };
    handleUrl();
    // location.href should NOT be a dependency below even if VS Code says so, it triggers the re-rendering twice /samuel
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedFilterString,
    debouncedCategoryString,
    node_locale,
    pageSlug,
    page,
  ]);

  useEffect(() => {
    isMobileSearchFilterOpen
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [isMobileSearchFilterOpen]);

  // Sort by priority
  let slicedSortedList = [];
  slicedSortedList = filteredCardList
    // ?.sort((a: any, b: any) => {
    //   return a.name
    //     ?.trim()
    //     .toLocaleLowerCase()
    //     .localeCompare(b.name?.trim().toLocaleLowerCase());
    // })
    ?.sort(sortByPriority)
    .slice(0, _pageSize * page);

  const dictionary = useGetLibraryConstantsDictionaryData();

  // Horizontal scroll
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [leftArrowDisable, setLeftArrowDisable] = useState(true);
  const [rightArrowDisable, setRightArrowDisable] = useState(true);

  const handleHorizontalScroll = (
    element: HTMLDivElement,
    speed: number,
    distance: number,
    step: number,
  ) => {
    let scrollAmount = 0;
    const slideTimer = setInterval(() => {
      const targetScrollLeft = element.scrollLeft + step;

      element.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
      scrollAmount += Math.abs(step);

      // Check if the scroll is at the left-most position
      if (element.scrollLeft === 0) {
        setLeftArrowDisable(true);
      } else {
        setLeftArrowDisable(false);
      }

      // Check if the scroll is at the right-most position
      if (
        Math.round(element.scrollLeft + element.clientWidth) >=
        element.scrollWidth
      ) {
        setRightArrowDisable(true);
      } else {
        setRightArrowDisable(false);
      }

      // Stop the scroll once the desired distance is covered
      if (scrollAmount > distance) {
        clearInterval(slideTimer);
      }
    }, speed);
  };

  // Initialize the arrows based on scrollable content
  const checkScrollArrows = (element: HTMLDivElement) => {
    if (element.scrollLeft === 0) {
      setLeftArrowDisable(true); // Disable left arrow at the start
    } else {
      setLeftArrowDisable(false); // Enable left arrow if scrolled
    }

    if (
      Math.round(element.scrollLeft + element.clientWidth) >=
      element.scrollWidth
    ) {
      setRightArrowDisable(true); // Disable right arrow if at the end
    } else {
      setRightArrowDisable(false); // Enable right arrow if there is more content to scroll
    }
  };

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (element) {
      checkScrollArrows(element); // Check the initial state of the arrows
    }
  }, []);

  const handleCategoryClick = (category: ListCategoryCard) => {
    if (category.isItAllCategories) {
      resetCategory();
    } else {
      setActiveFilterCategory({
        optionText: category.name,
        optionValue: category.name,
      });
    }
  };

  return (
    <main className="px-6 lg:px-0">
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        homeText={homeText}
        cssClass={isWebView ? '!pt-8' : ''}
        node_locale={node_locale}
      />
      <h1 className={'mt-4 font-title text-headingm text-text-default'}>
        {pageName}
      </h1>
      <div className="mt-4 flex justify-between items-start gap-6 sm:mt-10">
        {pageDescription && (
          <p className="w-full sm:w-[50%]">{pageDescription}</p>
        )}
        <InputField
          placeholder={`${dictionary?.find} ${pageName?.toLocaleLowerCase()}`}
          label=""
          disabled={false}
          type="text"
          name=""
          id=""
          value={activeFilterString ?? ''}
          onChange={(e) => {
            resetCategory();
            setActiveFilterString(e.currentTarget.value);
          }}
          icon="Search"
          className="relative ml-auto hidden max-w-[50%] flex-1 justify-end sm:block"
        />
      </div>
      <div className="my-10">
        <div className="flex justify-between">
          <h3 className="font-title text-headingm text-text-default">
            {categoryText}
          </h3>
          <div className="hidden gap-1 sm:flex">
            <ChevronLeftSmall
              className={'h-8 w-8 cursor-pointer'}
              onClick={() => {
                if (scrollContainerRef.current) {
                  handleHorizontalScroll(
                    scrollContainerRef.current,
                    25, // speed (in ms)
                    100, // distance (in px)
                    -100, // step (negative for left scroll)
                  );
                }
              }}
              style={{ opacity: leftArrowDisable ? 0.5 : 1 }}
            />

            {/* Right Chevron */}
            <ChevronRightSmall
              className={'h-8 w-8 cursor-pointer '}
              onClick={() => {
                if (scrollContainerRef.current) {
                  handleHorizontalScroll(
                    scrollContainerRef.current,
                    25, // speed (in ms)
                    100, // distance (in px)
                    100, // step (positive for right scroll)
                  );
                }
              }}
              style={{ opacity: rightArrowDisable ? 0.5 : 1 }}
            />
          </div>
        </div>
        <div
          className="scrollbar-mb mt-6 flex gap-4 overflow-x-auto scroll-smooth pb-6"
          ref={scrollContainerRef}
        >
          {categoryList?.length > 0 &&
            categoryList?.map((category, index) => {
              const isSelected =
                (index === 0 && activeFilterCategory?.optionValue === '') ||
                activeFilterCategory?.optionValue === category.name;
              return (
                <div
                  className="cursor-pointer flex-shrink-0 max-w-[146px]"
                  key={category.id}
                  onClick={() => handleCategoryClick(category)}
                >
                  {category?.image && (
                    <Image
                      src={category.image + '?fm=avif'}
                      className={`h-[120px] w-[146px] rounded-md object-cover ${
                        isSelected ? 'border-2 border-neutral-grey-700' : ''
                      }`}
                      alt=""
                      width={146}
                      height={120}
                      quality={60}
                    />
                  )}

                  <p
                    className={`mt-2 text-bodym ${
                      isSelected
                        ? 'font-title text-headingxs text-text-default'
                        : ''
                    }`}
                  >
                    {category?.name}
                  </p>
                </div>
              );
            })}
        </div>
      </div>

      {((activeFilterCategory && activeFilterCategory.optionValue.length > 0) ||
        (activeFilterString && activeFilterString.length > 0)) &&
      slicedSortedList.length === 0 ? (
        <NoHits
          text={dictionary?.noMatches ?? ''}
          node_locale={node_locale}
          pageName={pageName}
        />
      ) : (
        <TileView
          dictionary={dictionary}
          listArray={slicedSortedList}
          tileColClass={tileColClass}
          node_locale={node_locale}
          pageName={pageName}
          config={{}}
        />
      )}
      {subCategoryList?.length > _pageSize - 1 && (
        <ShowMore
          currentPage={page}
          maxNum={filteredCardList.length}
          setPage={(newPage) => {
            setScrollPositionOnFilter(window.scrollY);
            setPage(newPage);
          }}
          pageSize={_pageSize}
          ofString={dictionary?.of}
          showMoreString={dictionary?.showMore}
          showingString={dictionary?.showing}
        />
      )}
      {!isMobileSearchFilterOpen && (
        // hide filter button for the editorial listing page
        <button
          className={`${
            listType === 'editorial' ? 'hidden' : ''
          } smartbanner-top-margin fixed right-6 top-6 z-10 grid h-12 w-12 place-items-center rounded-full bg-brand-white shadow-down-right outline-0 transition-all focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:hidden lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark`}
          onClick={() => setIsMobileSearchFilterOpen(true)}
        >
          {activeFiltersCount > 0 && (
            <div className="absolute -right-1 -top-0.5 grid h-5 w-5 place-items-center rounded-full bg-brand-red-2 font-title text-buttonlabelxs text-brand-white">
              <span>{activeFiltersCount}</span>
            </div>
          )}
          <FilterIcon />
        </button>
      )}
      {isMobileSearchFilterOpen && (
        <Backdrop
          resetSearch={resetFilters}
          showButtons={true}
          filteredCardsCount={filteredCardList?.length}
          handleOpen={handleOpenMobileSearchFilter}
          clearAllString={dictionary?.clearAll}
          showResultsString={dictionary?.showResults}
          className={`
          lg:hidden${
            isMobileSearchFilterOpen
              ? ''
              : ' translate-y-[calc(100%+10rem)] lg:hidden lg:translate-y-0'
          }
        `}
        >
          <InputField
            placeholder={`${dictionary?.find} ${pageName?.toLocaleLowerCase()}`}
            label=""
            disabled={false}
            type="text"
            name=""
            id=""
            value={activeFilterString ?? ''}
            onChange={(e) => setActiveFilterString(e.currentTarget.value)}
            icon="Search"
            className="relative z-40 mb-4 flex-1"
          />

          <button
            className="absolute -top-20 right-4 z-40 grid h-12 w-12 place-items-center rounded-full bg-brand-grey-1 outline-0 transition-all focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:hidden lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark"
            onClick={() => setIsMobileSearchFilterOpen(false)}
          >
            <CloseIcon />
          </button>
        </Backdrop>
      )}
    </main>
  );
};

export default CustomListPage;
